import {
  getWsWireLog,
  subscribeWsWireLog,
  type WsWireLogEntry,
  WS_WIRE_LOG_STORAGE_KEY,
} from './ws-wire-log';
import { createFloatingPanel, type FloatingPanelHandle } from './dev/floating-panel';

export interface WsDebugOverlayHandle {
  readonly element: HTMLElement;
  dispose(): void;
}

export function shouldShowWsDebugOverlay(search = window.location.search): boolean {
  const params = new URLSearchParams(search);
  return params.get('debug') === 'ws';
}

export function createWsDebugOverlay(parent: HTMLElement = document.body): WsDebugOverlayHandle {
  let floatingPanel: FloatingPanelHandle | null = null;
  let disposed = false;

  const root = document.createElement('div');
  root.className = 'smashparty-ws-debug-overlay';
  root.style.cssText = [
    'width: min(720px, calc(100vw - 16px))',
    'max-height: min(520px, calc(100vh - 64px))',
    'background: rgba(6, 10, 18, 0.92)',
    'color: #f8fafc',
    'font: 12px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    'overflow: hidden',
    'pointer-events: auto',
  ].join('; ');
  root.addEventListener('pointerdown', stopHostInteraction);
  root.addEventListener('pointerup', stopHostInteraction);
  root.addEventListener('click', stopHostInteraction);
  root.addEventListener('wheel', stopHostInteraction);
  root.addEventListener('contextmenu', stopHostInteraction);

  const header = document.createElement('button');
  header.type = 'button';
  header.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: space-between',
    'gap: 12px',
    'width: 100%',
    'border: 0',
    'padding: 8px 10px',
    'background: rgba(15, 23, 42, 0.95)',
    'color: inherit',
    'font: inherit',
    'cursor: pointer',
  ].join('; ');

  const title = document.createElement('span');
  const subtitle = document.createElement('span');
  subtitle.style.cssText = 'color: rgba(226,232,240,0.72); font-size: 11px';
  header.append(title, subtitle);

  const list = document.createElement('div');
  list.style.cssText = [
    'display: none',
    'max-height: 470px',
    'overflow: auto',
    'padding: 8px',
  ].join('; ');

  root.append(header, list);
  floatingPanel = createFloatingPanel({
    id: 'ws-debug-overlay',
    title: 'WS Debug',
    defaultPosition: { x: 20, y: window.innerHeight - 420 },
    contentRoot: root,
    parent,
    onClose: dispose,
  });

  let expanded = false;
  let entries = [...getWsWireLog()];
  const expandedEntryIds = new Set<number>();

  function render(): void {
    title.textContent = `WS log (${entries.length})`;
    subtitle.textContent = `${expanded ? 'collapse' : 'expand'} | ${WS_WIRE_LOG_STORAGE_KEY}`;
    list.style.display = expanded ? 'block' : 'none';
    if (!expanded) return;

    pruneExpandedEntries(expandedEntryIds, entries);
    list.replaceChildren();
    for (const entry of entries.slice(-80).reverse()) {
      list.appendChild(
        renderEntry(entry, expandedEntryIds.has(entry.id), () => {
          if (expandedEntryIds.has(entry.id)) {
            expandedEntryIds.delete(entry.id);
          } else {
            expandedEntryIds.add(entry.id);
          }
          render();
        }),
      );
    }
  }

  header.addEventListener('click', () => {
    expanded = !expanded;
    render();
  });

  const unsubscribe = subscribeWsWireLog((_entry, nextEntries) => {
    entries = [...nextEntries];
    render();
  });

  render();

  return {
    element: floatingPanel.root,
    dispose,
  };

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    unsubscribe();
    root.removeEventListener('pointerdown', stopHostInteraction);
    root.removeEventListener('pointerup', stopHostInteraction);
    root.removeEventListener('click', stopHostInteraction);
    root.removeEventListener('wheel', stopHostInteraction);
    root.removeEventListener('contextmenu', stopHostInteraction);
    floatingPanel?.dispose();
  }
}

function renderEntry(entry: WsWireLogEntry, expanded: boolean, onToggle: () => void): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = ['border-top: 1px solid rgba(148, 163, 184, 0.22)', 'padding: 6px 0'].join(
    '; ',
  );

  const summary = document.createElement('button');
  summary.type = 'button';
  summary.setAttribute('aria-expanded', String(expanded));
  summary.style.cssText = [
    'cursor: pointer',
    'display: grid',
    'grid-template-columns: 86px 48px minmax(80px, 1fr) minmax(80px, 2fr)',
    'gap: 8px',
    'align-items: center',
    'width: 100%',
    'border: 0',
    'padding: 0',
    'background: transparent',
    'color: inherit',
    'font: inherit',
    'text-align: left',
    'white-space: nowrap',
  ].join('; ');
  summary.addEventListener('click', (event) => {
    event.stopPropagation();
    onToggle();
  });

  const time = document.createElement('span');
  time.textContent = entry.at.slice(11, 23);
  time.style.color = 'rgba(226,232,240,0.72)';

  const dir = document.createElement('span');
  dir.textContent = directionLabel(entry);
  dir.style.color =
    entry.direction === 'out' ? '#93c5fd' : entry.direction === 'in' ? '#86efac' : '#fca5a5';

  const type = document.createElement('span');
  type.textContent = entryTitle(entry);
  type.style.overflow = 'hidden';
  type.style.textOverflow = 'ellipsis';

  const stage = document.createElement('span');
  stage.textContent = entry.detail != null ? `${entry.stage}: ${entry.detail}` : entry.stage;
  stage.style.color = 'rgba(226,232,240,0.82)';
  stage.style.overflow = 'hidden';
  stage.style.textOverflow = 'ellipsis';

  summary.append(time, dir, type, stage);

  const pre = document.createElement('pre');
  pre.hidden = !expanded;
  pre.textContent = formatExpandedPayload(entry);
  pre.style.cssText = [
    'margin: 6px 0 0',
    'padding: 8px',
    'background: rgba(2, 6, 23, 0.74)',
    'border-radius: 4px',
    'overflow: auto',
    'white-space: pre-wrap',
    'word-break: break-word',
  ].join('; ');

  row.append(summary, pre);
  return row;
}

function directionLabel(entry: WsWireLogEntry): string {
  if (entry.direction === 'out') return 'send';
  if (entry.direction === 'in') return 'recv';
  return 'error';
}

function stopHostInteraction(event: Event): void {
  event.stopPropagation();
}

function pruneExpandedEntries(
  expandedEntryIds: Set<number>,
  entries: readonly WsWireLogEntry[],
): void {
  const visibleIds = new Set(entries.map((entry) => entry.id));
  for (const id of expandedEntryIds) {
    if (!visibleIds.has(id)) {
      expandedEntryIds.delete(id);
    }
  }
}

function entryTitle(entry: WsWireLogEntry): string {
  const type = entry.type ?? '(no type)';
  if (entry.type !== 'notification') return type;

  const summary = notificationSummary(entry.payload);
  return summary.length > 0 ? [type, ...summary].join(' | ') : type;
}

function notificationSummary(payload: unknown): string[] {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return [];
  const record = payload as Record<string, unknown>;
  return [formatInlineValue(record.level), formatInlineValue(record.text)].filter(
    (value): value is string => value !== null,
  );
}

function formatInlineValue(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function formatExpandedPayload(entry: WsWireLogEntry): string {
  if (entry.payload !== undefined) {
    return JSON.stringify(entry.payload, null, 2);
  }
  if (entry.raw !== undefined) return entry.raw;
  return JSON.stringify(
    {
      at: entry.at,
      direction: entry.direction,
      stage: entry.stage,
      type: entry.type,
      detail: entry.detail,
    },
    null,
    2,
  );
}
